import ClientPreviewPanel from './ClientPreviewPanel';
import ClientRow from './ClientRow';
import { ActionBar, Box, Button, Center, Checkbox, Flex, Input, InputGroup, ProgressCircle, Separator, Spacer, Stack, Table, Text } from '@chakra-ui/react';
import { Client } from '@opanuj/shared/types';
import { FiCalendar, FiEdit2, FiPlusSquare, FiTrash2, FiX } from 'react-icons/fi';
import { getActiveOnlyFromSearch, getSortByFromSearch, getSortDirectionFromSearch, isClientActive, matchesClientQuery, sortClients } from './clientTableUtils';
import { NewAccountModal } from '../../../components/NewAccountModal';
import { productDelete } from '../../../operations/admin/productDelete';
import { ref, remove } from 'firebase/database';
import { setDocumentTitle } from '../../../utils/documentTitle';
import { toaster } from '../../../components/ui/toaster';
import { Tooltip } from '../../../components/ui/tooltip';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useClientsList } from '../../../hooks/useClients';
import { useDatabase } from '../../../hooks/useDatabase';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useReservationsList } from '../../../hooks/useReservations';
import { useSlotsList } from '../../../hooks/useSlots';

const columns = [
	{ key: 'createdAt', label: 'Rejestracja' },
	{ key: 'hasAccountAccess', label: 'Pełne konto' },
	{ key: 'openedReservations', label: 'Rezerw.' },
	{ key: 'products', label: 'Zajęcia' },
	{ key: 'lessonsPool', label: 'Do odrob.' },
	{ key: 'name', label: 'Imię i nazwisko' },
	{ key: 'email', label: 'Email' },
	{ key: 'phone', label: 'Telefon' },
];

const ClientsPage = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { clientId: routeClientId } = useParams();
	useEffect(setDocumentTitle('Klienci')); // eslint-disable-line react-hooks/exhaustive-deps
	const [searchQuery, setSearchQuery] = useState('');
	const [activeOnly, setActiveOnly] = useState(() => getActiveOnlyFromSearch(location.search));
	const [sortBy, setSortBy] = useState<string>(() => getSortByFromSearch(location.search, columns.map((column) => column.key), 'name'));
	const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(() => getSortDirectionFromSearch(location.search, 'asc'));
	const [selectedClientIds, setSelectedClientIds] = useState<string[]>(() => routeClientId ? [routeClientId] : []);
	const [previewClient, setPreviewClient] = useState<Client | null>(null);
	const [isClientEditorOpen, setIsClientEditorOpen] = useState(false);
	const [isReservationOpen, setIsReservationOpen] = useState(false);
	const [editingClient, setEditingClient] = useState<Client | null>(null);
	const [clients, clientsLoading, clientsError] = useClientsList();
	const [reservations, reservationsLoading, reservationsError] = useReservationsList();
	const [slots, slotsLoading, slotsError] = useSlotsList();

	const openPreview = useCallback((client: Client | null) => {
		if (!client) {
			setPreviewClient(null);
			navigate({ pathname: '/admin/klienci', search: location.search }, { replace: true });
			return;
		}

		setSelectedClientIds((prev) => prev.includes(client.id) ? prev : [...prev, client.id]);
		setPreviewClient(client);
		navigate({ pathname: `/admin/klienci/${client.id}`, search: location.search }, { replace: true });
	}, [location.search, navigate]);

	useEffect(() => {
		if (clientsLoading) {
			return;
		}

		if (selectedClientIds.length !== 1) {
			if (routeClientId) {
				navigate({ pathname: '/admin/klienci', search: location.search }, { replace: true });
			}
			if (previewClient) {
				setPreviewClient(null);
			}
			return;
		}

		const selectedClient = (clients ?? []).find((item: Client) => item.id === selectedClientIds[0]) ?? null;
		if (!selectedClient) {
			if (routeClientId) {
				navigate({ pathname: '/admin/klienci', search: location.search }, { replace: true });
			}
			if (previewClient) {
				setPreviewClient(null);
			}
			return;
		}

		if (previewClient?.id !== selectedClient.id) {
			setPreviewClient(selectedClient);
		}

		if (routeClientId !== selectedClient.id) {
			navigate({ pathname: `/admin/klienci/${selectedClient.id}`, search: location.search }, { replace: true });
		}
	}, [clients, clientsLoading, navigate, previewClient, routeClientId, selectedClientIds, location.search]);

	useEffect(() => {
		if (clientsLoading) {
			return;
		}

		setSelectedClientIds((prev) => {
			const next = prev.filter((id) => (clients ?? []).some((client: Client) => client.id === id));
			const isSame = next.length === prev.length && next.every((id, index) => id === prev[index]);
			return isSame ? prev : next;
		});
	}, [clients, clientsLoading]);

	const filteredClients = useMemo(() => {
		const list = clients ?? [];
		const counted = list.map((client: Client) => ({
			...client,
			openedReservations: (reservations ?? [])
				.filter((reservation) => reservation?.uid === client?.uid)
				.reduce((total, reservation) => total + (reservation?.lessons?.length ?? 0), 0),
		}));
		const activeClients = activeOnly ? counted.filter((client) => isClientActive(client, client.openedReservations ?? 0)) : counted;
		const searched = activeClients.filter((client: Client & { openedReservations?: number }) => matchesClientQuery(client, searchQuery));

		return sortClients(searched, sortBy, sortDirection);
	}, [activeOnly, clients, reservations, searchQuery, sortBy, sortDirection]);

	const handleSort = (column: string) => {
		if (sortBy === column) {
			setSortDirection((prevDirection) => (prevDirection === 'asc' ? 'desc' : 'asc'));
		} else {
			setSortBy(column);
			setSortDirection('asc');
		}
	};

	const selectClient = useCallback((clientId: string) => {
		const nextClient = (clients ?? []).find((client: Client) => client.id === clientId) ?? null;
		setSelectedClientIds([clientId]);
		setPreviewClient(nextClient);
		navigate({
			pathname: nextClient ? `/admin/klienci/${nextClient.id}` : '/admin/klienci',
			search: location.search,
		}, { replace: true });
	}, [clients, location.search, navigate]);

	const toggleSelection = useCallback((clientId: string, checked: boolean) => {
		setSelectedClientIds((prev) => {
			if (checked) {
				return prev.includes(clientId) ? prev : [...prev, clientId];
			}
			return prev.filter((id) => id !== clientId);
		});
	}, []);

	const selectedPreviewClient = useMemo(() => {
		if (selectedClientIds.length !== 1) {
			return null;
		}
		return (clients ?? []).find((client: Client) => client.id === selectedClientIds[0]) ?? null;
	}, [clients, selectedClientIds]);

	const visibleClientIds = useMemo(
		() => filteredClients.map((client: Client) => client.id),
		[filteredClients],
	);
	const allVisibleSelected = useMemo(
		() => visibleClientIds.length > 0 && visibleClientIds.every((clientId) => selectedClientIds.includes(clientId)),
		[visibleClientIds, selectedClientIds],
	);
	const someVisibleSelected = useMemo(
		() => visibleClientIds.some((clientId) => selectedClientIds.includes(clientId)),
		[visibleClientIds, selectedClientIds],
	);

	const toggleVisibleSelection = useCallback((checked: boolean) => {
		setSelectedClientIds((prev) => {
			if (checked) {
				return Array.from(new Set([...prev, ...visibleClientIds]));
			}
			return prev.filter((id) => !visibleClientIds.includes(id));
		});
	}, [visibleClientIds]);

	const clearSelection = useCallback(() => {
		setSelectedClientIds([]);
		setPreviewClient(null);
		navigate({ pathname: '/admin/klienci', search: location.search }, { replace: true });
	}, [location.search, navigate]);

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape' && selectedClientIds.length > 0) {
				event.preventDefault();
				clearSelection();
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [clearSelection, selectedClientIds.length]);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const nextValue = params.get('active') === '1' || params.get('active') === 'true';
		setActiveOnly((prev) => (prev === nextValue ? prev : nextValue));
	}, [location.search]);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const nextValue = activeOnly ? '1' : '0';
		const hasActiveParam = params.has('active');
		const isActiveParamSame = params.get('active') === nextValue;

		if (!hasActiveParam && !activeOnly) {
			return;
		}

		if (isActiveParamSame) {
			return;
		}

		navigate(
			{
				pathname: location.pathname,
				search: new URLSearchParams({
					...Object.fromEntries(params.entries()),
					active: nextValue,
				}).toString(),
			},
			{ replace: true },
		);
	}, [activeOnly, location.pathname, location.search, navigate]);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const nextSortBy = sortBy;
		const nextSortDirection = sortDirection;
		const hasSortByParam = params.has('sortBy');
		const hasSortDirParam = params.has('sortDir');
		const isSortBySame = params.get('sortBy') === nextSortBy;
		const isSortDirSame = params.get('sortDir') === nextSortDirection;

		if (!hasSortByParam && !hasSortDirParam && nextSortBy === 'name' && nextSortDirection === 'asc') {
			return;
		}

		if (isSortBySame && isSortDirSame) {
			return;
		}

		navigate(
			{
				pathname: location.pathname,
				search: new URLSearchParams({
					...Object.fromEntries(params.entries()),
					sortBy: nextSortBy,
					sortDir: nextSortDirection,
				}).toString(),
			},
			{ replace: true },
		);
	}, [location.pathname, location.search, navigate, sortBy, sortDirection]);

	const openAddClient = () => {
		setEditingClient(null);
		setIsClientEditorOpen(true);
	};

	const openEditClient = (client: Client | null) => {
		setEditingClient(client ?? null);
		setIsClientEditorOpen(Boolean(client));
	};

	const database = useDatabase();
	const handleProductDelete = useCallback(async (slotId: string) => {
		if (!previewClient?.id) return;
		const shouldDelete = window.confirm('Czy na pewno chcesz usunąć te aktywne zajęcia?');
		if (!shouldDelete) return;

		try {
			await productDelete(database, {
				clientId: previewClient.id,
				slotId,
				clientUid: previewClient.uid,
			});
			setPreviewClient((currentClient) => {
				if (!currentClient) return currentClient;

				const remainingProducts = Object.fromEntries(
					Object.entries(currentClient.products ?? {}).filter(([, product]) => product.slotId !== slotId),
				);

				return { ...currentClient, products: remainingProducts };
			});
			toaster.create({
				title: 'Aktywne zajęcia usunięte',
				type: 'success',
				duration: 2500,
			});
		} catch (error) {
			console.error('Product delete failed', error);
			toaster.create({
				title: 'Nie udało się usunąć zajęć',
				type: 'error',
				duration: 3000,
			});
		}
	}, [database, previewClient]);

	const renderClientDetails = (client: Client) => (
		<ClientPreviewPanel
			client={client}
			reservations={reservations ?? []}
			slots={slots ?? []}
			isReservationOpen={isReservationOpen}
			onOpenReservation={() => setIsReservationOpen(true)}
			onCloseReservation={() => setIsReservationOpen(false)}
			onProductDelete={handleProductDelete}
		/>
	);

	const handleClientDelete = useCallback(async () => {
		if (!previewClient) {
			return;
		}

		if (previewClient.uid) {
			return;
		}

		const shouldDelete = window.confirm(`Czy na pewno chcesz usunąć klienta ${previewClient.name || 'bez nazwy'}?`);
		if (!shouldDelete) {
			return;
		}

		const clientPath = previewClient.id ? `clients/${previewClient.id}` : null;
		if (!clientPath) {
			return;
		}

		try {
			await remove(ref(database, clientPath));
			setPreviewClient(null);
			navigate({ pathname: '/admin/klienci', search: location.search }, { replace: true });
			setSelectedClientIds((prev) => prev.filter((id) => id !== previewClient.id));
			toaster.create({
				title: 'Klient usunięty',
				type: 'success',
				duration: 2500,
			});
		} catch (error) {
			console.error('Client delete failed', error);
			toaster.create({
				title: 'Nie udało się usunąć klienta',
				type: 'error',
				duration: 3000,
			});
		}
	}, [database, location.search, navigate, previewClient]);

	if (clientsLoading || reservationsLoading || slotsLoading) {
		return (
			<Center my={12}>
				<ProgressCircle.Root value={null}>
					<ProgressCircle.Circle>
						<ProgressCircle.Track />
						<ProgressCircle.Range stroke="orange.400" />
					</ProgressCircle.Circle>
				</ProgressCircle.Root>
			</Center>
		);
	}

	if (clientsError || reservationsError || slotsError) {
		console.error(clientsError || reservationsError || slotsError);
		return null;
	}

	return (
		<>
			<Box p={4} position="sticky" top={0} zIndex={10} bg="white" boxShadow="sm">
				<Stack direction="row" align="center" gap={3}>
					<InputGroup flex={1} endElement={searchQuery ? (
						<Button
							size="xs"
							variant="ghost"
							onClick={() => setSearchQuery('')}
							aria-label="Wyczyść wyszukiwanie"
						>
							<FiX />
						</Button>
					) : undefined}>
						<Input
							placeholder="Szukaj po imieniu lub emailu"
							value={searchQuery}
							onChange={(event) => setSearchQuery(event.target.value)}
						/>
					</InputGroup>
					<Checkbox.Root
						checked={activeOnly}
						onCheckedChange={(details) => setActiveOnly(details.checked === true)}
					>
						<Checkbox.HiddenInput />
						<Checkbox.Control />
						<Checkbox.Label>Tylko aktywni</Checkbox.Label>
					</Checkbox.Root>
					<Separator orientation="vertical" height={8}/>
					<Button size="sm" variant="surface" colorPalette="green" onClick={openAddClient}>
						<FiPlusSquare /> Dodaj klienta
					</Button>
				</Stack>
			</Box>

			<Flex gap={4} align="stretch" px={4}>
				<Box flex="2" minW={0}>
					<Table.ScrollArea maxH="calc(100vh - 160px)" style={{ overflowY: 'auto' }}>
						<Table.Root size='sm'>
							<Table.Header>
								<Table.Row>
									<Table.ColumnHeader
										cursor="pointer"
										fontWeight="bold"
										userSelect="none"
										position="sticky"
										top={0}
										bg="white"
										zIndex={1}
									>
										<Checkbox.Root
											checked={someVisibleSelected ? (allVisibleSelected ? true : 'indeterminate') : false}
											onCheckedChange={(details) => {
												const checked = details.checked === true;
												toggleVisibleSelection(checked);
											}}
										>
											<Checkbox.HiddenInput />
											<Checkbox.Control />
										</Checkbox.Root>
									</Table.ColumnHeader>
									{columns.map((column) => {
										const isActive = sortBy === column.key;
										const sortArrow = isActive ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : '';
										return (
											<Table.ColumnHeader
												key={column.key}
												onClick={() => handleSort(column.key)}
												cursor="pointer"
												fontWeight="bold"
												userSelect="none"
												position="sticky"
												top={0}
												bg="white"
												zIndex={1}
											>
												{column.label}{sortArrow}
											</Table.ColumnHeader>
										);
									})}
									<Table.ColumnHeader
										w="100%"
										minW="100%"
										position="sticky"
										top={0}
										bg="white"
										zIndex={1}
									/>
								</Table.Row>
							</Table.Header>
							<Table.Body>
								{filteredClients.map((client: Client & { openedReservations?: number }) => {
									return (
										<ClientRow
											key={client.id}
											client={client}
											openedReservations={client.openedReservations ?? 0}
											selected={selectedClientIds.includes(client.id)}
											previewed={previewClient?.id === client.id}
											onSelectClient={selectClient}
											onToggleSelection={toggleSelection}
											onOpenDetails={openPreview}
										/>
									);
								})}
							</Table.Body>
						</Table.Root>
					</Table.ScrollArea>
				</Box>

				<Box
					flex="1"
					minW="320px"
					maxW="40%"
					bg="white"
					borderLeft="1px solid"
					borderColor="gray.200"
					overflow="hidden"
				>
					{previewClient ? (
						<Stack h="calc(100vh - 160px)" minH="420px">
							<Box borderBottom="1px solid" borderColor="gray.200" px={4} py={3}>
								<Text fontSize="lg" fontWeight="semibold">{previewClient.name || 'Szczegóły klienta'}</Text>
							</Box>
							<Box flex={1} overflowY="auto" px={4} py={4}>
								{renderClientDetails(previewClient)}
							</Box>
							<Box borderTop="1px solid" borderColor="gray.200" px={4} py={3}>
								<Flex align="center" gap={2}>
									<Button
										colorPalette="orange"
										variant="surface"
										onClick={() => setIsReservationOpen(true)}
									>
										<FiCalendar />
										Rezerwuj
									</Button>
									<Spacer />
									<Button variant="outline" onClick={() => openEditClient(previewClient)}>
										<FiEdit2 />
										Edytuj
									</Button>
									<Tooltip
										content="Usuwanie klientów z dostępem do systemu aktualnie jest niemożliwe."
										disabled={!Boolean(previewClient?.uid)}
									>
										<span>
											<Button
												colorPalette="red"
												variant="outline"
												disabled={Boolean(previewClient?.uid)}
												onClick={handleClientDelete}
											>
												<FiTrash2 />
												Usuń
											</Button>
										</span>
									</Tooltip>
								</Flex>
							</Box>
						</Stack>
					) : (
						<Center h="calc(100vh - 160px)" minH="420px" px={6}>
							<Text textAlign="center" color="gray.500" fontSize="md">
								{selectedClientIds.length > 1 ? 'Wybierz pojedynczego klienta, aby zobaczyć szczegóły' : 'Wybierz klienta z listy, aby zobaczyć szczegóły'}
							</Text>
						</Center>
					)}
				</Box>
			</Flex>

			<ActionBar.Root
				open={selectedClientIds.length > 0}
			>
				<ActionBar.Positioner>
					<ActionBar.Content>
						<ActionBar.SelectionTrigger>
							<Text fontSize="sm" fontWeight="semibold">
								{selectedClientIds.length} {selectedClientIds.length === 1 ? 'zaznaczony' : 'zaznaczonych'}
							</Text>
						</ActionBar.SelectionTrigger>
						<ActionBar.Separator />
						{selectedPreviewClient && (
							<Button size="sm" variant="surface" colorPalette="cyan" onClick={() => openPreview(selectedPreviewClient)}>
								Szczegóły
							</Button>
						)}
						{/* <Button size="sm" colorPalette="red" variant="outline" onClick={handleDeleteSelected}>Usuń</Button>
						<Button size="sm" colorPalette="green" onClick={handleInvoiceSelected}>Wystaw fakturę</Button> */}
						<ActionBar.CloseTrigger asChild>
							<Button size="sm" variant="ghost" onClick={clearSelection}>Odznacz</Button>
						</ActionBar.CloseTrigger>
					</ActionBar.Content>
				</ActionBar.Positioner>
			</ActionBar.Root>

			<NewAccountModal
				isOpen={isClientEditorOpen}
				onClose={() => {
					setIsClientEditorOpen(false);
					setEditingClient(null);
				}}
				mode={editingClient ? 'admin-edit' : 'admin'}
				initialClient={editingClient ?? undefined}
			/>

		</>
	);
};

export default ClientsPage;